import BaseReferenceGenerator from './BaseReferenceGenerator';
import { FieldReferences, MappingReferenceData } from './types';
import { SheetAttributeData } from '../sheet-attribute-manager/models/sheet-attribute-manager.interface';

export enum CAPTABLE_MODEL_TYPE {
  SECURITY = 'security',
}

export interface CapTableGeneratedReferences {
  securityReferences: FieldReferences;
}

class CapTableReferenceGenerator extends BaseReferenceGenerator<CapTableGeneratedReferences> {
  private securityFieldAliases: Set<string>;

  constructor(
    securityFields: string[] = [],
  ) {
    super();
    this.securityFieldAliases = new Set(securityFields);
  }

  /**
   * Determine if a field belongs to security, holder, or transaction
   */
  getFieldModelType(fieldName: string): CAPTABLE_MODEL_TYPE | null {
    if (this.securityFieldAliases.has(fieldName)) {
      return CAPTABLE_MODEL_TYPE.SECURITY;
    }

    return null;
  }

  /**
   * Generate mapping references from sheet attribute data for cap table
   */
  generateMappingReferences(sheetAttributeData: SheetAttributeData): CapTableGeneratedReferences {
    const securityReferences: FieldReferences = {};

    // Iterate over the data property (could be measurement dates or other identifiers)
    Object.keys(sheetAttributeData).forEach(identifier => {
      const fieldsData = sheetAttributeData[identifier];

      // For each field, determine its type
      Object.keys(fieldsData).forEach(fieldName => {
        const fieldData = fieldsData[fieldName];
        const modelType = this.getFieldModelType(fieldName);

        if (!modelType) {
          return; // Skip unknown fields
        }

        // For each referenceData item
        const referenceDataList = fieldData.referenceData || [];

        const mappingReferences: MappingReferenceData[] = [];

        referenceDataList.forEach(refDataItem => {
          const { documentReferenceId } = refDataItem;
          const sheetMappingConfig = refDataItem.sheetMappingConfig || {};

          if (!documentReferenceId) {
            return;
          }

          // For each sheetMappingConfig
          Object.keys(sheetMappingConfig).forEach(sheetName => {
            const sheetConfig = sheetMappingConfig[sheetName];
            const mappedCells = sheetConfig.mappedCells || [];

            if (mappedCells.length === 0) {
              return;
            }

            // Create a mapping reference
            mappingReferences.push({
              document_reference: documentReferenceId,
              field_name: fieldName,
              sheet_name: sheetName,
              cell_references: mappedCells,
            });
          });
        });

        // Store references in the appropriate structure
        if (!securityReferences[identifier]) {
          securityReferences[identifier] = {};
        }
        securityReferences[identifier][fieldName] = mappingReferences;
      });
    });

    return {
      securityReferences,
    };
  }

  /**
   * Get reference metadata for a specific cell in the cap table
   */
  getReferenceMetadataForCell(
    identifier: string,
    fieldName: string,
    references: CapTableGeneratedReferences
  ): MappingReferenceData[] | null {
    // Check security references
    const securityRefs = references.securityReferences[identifier]?.[fieldName];
    if (securityRefs && securityRefs.length > 0) {
      return securityRefs;
    }

    return null;
  }
}

export default CapTableReferenceGenerator;

